import { useContext, useEffect, useState } from "react";
import Swimlane from "../components/Swimlane";
import { getTasks } from "../services/api-server/tasks";
import { TOperationData } from "../types/operation";
import { TTaskData } from "../types/task";
import { TWellData } from "../types/well";
import { GetAntIcon } from "../utils/ant_icons";
import { HomeContext } from "./Home";

interface ColumnInterface {
	status: string;
	droppable: boolean;
	tasks: TTaskData[];
}

function MultiAssetPhaseKanbanBoard({
	operationList,
	phase_id,
}: {
	operationList: (TOperationData & TWellData)[];
	phase_id: string;
}) {
	const taskStatusList = [
		{
			columnName: "Backlog",
			status: "New",
			droppable: true,
		},
		{
			columnName: "To do",
			status: "To do",
			droppable: true,
		},
		{
			columnName: "In progress",
			status: "In progress",
			droppable: true,
		},
		{
			columnName: "Late",
			status: "Late",
			droppable: true,
		},
		{
			columnName: "Resolved",
			status: "Resolved",
			droppable: true,
		},
		{
			columnName: "Closed",
			status: "Closed",
			droppable: true,
		},
	];

	const { currentTenantKey }: any = useContext(HomeContext);

	// State to store tasks for each swimlane
	const [projectTasks, setProjectTasks] = useState<ColumnInterface[]>([]);
	const [operationTasks, setOperationTasks] = useState<{
		[key: string]: ColumnInterface[];
	}>({});

	useEffect(() => {
		console.log("projectTasks", projectTasks);
	}, [projectTasks]);

	const fetchTasks = async (filter: any) => {
		const tasks: any = await getTasks(currentTenantKey, filter);
		const groupedTasks: ColumnInterface[] = taskStatusList.map((status) => {
			return {
				status: status.status,
				droppable: status.droppable,
				tasks:
					tasks?.filter((task: TTaskData) => {
						if (status.status === "Closed") return task.closed === true;
						return task.status === status.status && task.closed !== true;
					}) || [],
			};
		});
		return groupedTasks;
	};

	// Fetch tasks for project swimlane
	const fetchProjectTasks = async () => {
		const tasks = await fetchTasks({
			phase_id: phase_id,
			operation_id: "null",
		});
		setProjectTasks(tasks);
	};

	// Fetch tasks for operation swimlanes
	const fetchOperationTasks = async (operation: TOperationData & TWellData) => {
		const tasks = await fetchTasks({
			phase_id: phase_id,
			operation_id: operation._id,
		});
		setOperationTasks((prev) => ({
			...prev,
			[operation._id]: tasks,
		}));
	};

	// Fetch all tasks on component mount
	useEffect(() => {
		const loadAllTasks = async () => {
			// Fetch project tasks
			await fetchProjectTasks();
			// Fetch tasks for each operation
			const operationPromises = operationList.map((operation) =>
				fetchOperationTasks(operation)
			);
			await Promise.all(operationPromises);
		};

		loadAllTasks();
	}, [phase_id, operationList, currentTenantKey]);

	return (
		<div className="kanban-board-container">
			<div className="kanban-columns-container">
				<div
					style={{
						display: "flex",
						flexDirection: "column",
					}}
				>
					<div style={{ display: "flex", gap: "30px" }}>
						{taskStatusList.map((status) => (
							<div
								key={status.status}
								style={{
									minWidth: "320px",
									maxWidth: "320px",
									height: "100%",
									display: "flex",
									flexDirection: "column",
									gap: "30px",
								}}
							>
								<div className="phase-container">
									<div className={"project-status-container-header "}>
										{GetAntIcon("square-check")}
										<span>{status.columnName}</span>
									</div>
								</div>
							</div>
						))}
					</div>
					<Swimlane
						label={"Project"}
						defaultOpen={true}
						groupedTasks={projectTasks}
					/>
					{operationList.map((operation) => {
						return (
							<Swimlane
								key={operation._id}
								label={`${operation.well_name} > ${operation.operation_name}`}
								groupedTasks={operationTasks[operation._id] || []}
								operation={operation}
							/>
						);
					})}
				</div>
			</div>
		</div>
	);
}

export default MultiAssetPhaseKanbanBoard;
